// ================================================================
// authShell — der gemeinsame PLANMOVE-Rahmen der öffentlichen Seiten
// ================================================================
// Login (AuthGate), Einladung annehmen (InviteAccept) und Passwort
// zurücksetzen (ResetPassword) sehen identisch aus: Sprachwahl oben rechts,
// Marken-Spalte mit Hintergrundbild, weisse Karte, Sicherheitshinweis unten.
// Der Rahmen lebt deshalb genau einmal hier — vorher war er in zwei Dateien
// kopiert, und die dritte hätte die dritte Kopie gebracht.

import { t, getLanguage, setLanguage, getAvailableLanguages } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';

/**
 * Umschliesst den Karten-Inhalt mit dem Marken-Rahmen.
 *
 * `opts.brand` = HTML für die Marken-Zone oben, `opts.cobrand` = unsere Marke
 * tritt zurück (Hintergrundbild OHNE eingebranntes PLANMOVE-Logo). Genau das
 * braucht die öffentliche Dokument-Seite — dort ist der Kunde beim HANDWERKER,
 * nicht bei uns; unser Logo über seiner Offerte war schlicht die falsche Marke.
 *
 * `cobrand` steht getrennt von `brand`, weil die Kopfdaten erst nachgeladen
 * werden: ohne das Flag zeigte die Seite einen Moment lang PLANMOVE und
 * tauschte die Marke dann vor den Augen des Kunden aus.
 *
 * Login, Einladung und Passwort-Reset lassen beides weg und bleiben, wie sie
 * sind: dort ist PLANMOVE tatsächlich der Absender.
 */
export function authShell(cardHtml, opts = {}) {
  const langs = getAvailableLanguages();
  const current = getLanguage();
  const brand = opts.brand || '';
  const cobrand = opts.cobrand || !!brand;
  return `
    <div class="auth-gate pm${cobrand ? ' pm-cobrand' : ''}">
      <div class="auth-lang">
        <select class="auth-lang-select" data-lang-select aria-label="Language">
          ${langs.map((l) => `
            <option value="${l.code}" ${l.code === current ? 'selected' : ''}>${escapeHtml(l.name)}</option>
          `).join('')}
        </select>
      </div>

      <div class="auth-shell">
        <div class="auth-brandspace">${brand}</div>
        <div class="auth-card">${cardHtml}</div>
        <p class="auth-secure">${icon('lock', 15)}<span>${escapeHtml(t('auth.secureNote'))}</span></p>
      </div>
    </div>`;
}

/**
 * Hängt den Sprachumschalter an. `redraw` wird nach dem Laden der Sprachdatei
 * gerufen (die Dateien werden lazy geladen — vorher zu zeichnen zeigt die alte
 * Sprache).
 */
export function bindLangSelect(rootEl, redraw) {
  rootEl.querySelector('[data-lang-select]')?.addEventListener('change', async (e) => {
    await setLanguage(e.target.value);
    redraw();
  });
}

/**
 * Zeichnet neu, ohne dem Nutzer das Getippte wegzunehmen.
 *
 * Die öffentlichen Seiten sind Vanilla: JEDER Zustandswechsel — Fehlermeldung,
 * Auge zum Passwort-Anzeigen, Sprachwechsel — ersetzt die Karte per innerHTML.
 * Die Felder stehen danach leer da. Im Alltag heisst das: man tippt sein
 * Passwort, klickt aufs Auge um zu prüfen, ob man sich vertippt hat — und alles
 * ist weg. Oder man vertippt sich beim Login und darf auch die E-Mail neu
 * eingeben. Genau an der Stelle, an der jemand ohnehin schon kämpft.
 *
 * Darum: Werte und Cursor VOR dem Neuzeichnen einsammeln, danach zurückschreiben
 * — adressiert über `name`. Überschrieben wird nur, was die neue Ansicht leer
 * lässt; setzt sie ein Feld absichtlich (Vorbelegung), gewinnt sie.
 */
export function redrawKeepingInput(rootEl, render) {
  const fields = () => rootEl.querySelectorAll('input[name], textarea[name]');
  const before = new Map();
  let focusName = null;
  let selStart = null;
  let selEnd = null;

  for (const el of fields()) {
    before.set(el.name, el.value);
    if (el === document.activeElement) {
      focusName = el.name;
      selStart = el.selectionStart;
      selEnd = el.selectionEnd;
    }
  }

  render();

  for (const el of fields()) {
    if (!el.value && before.has(el.name)) el.value = before.get(el.name);
  }

  if (focusName) {
    const el = rootEl.querySelector(`[name="${CSS.escape(focusName)}"]`);
    if (el) {
      el.focus();
      // setSelectionRange wirft bei type=email/number — dort reicht der Fokus.
      try { el.setSelectionRange(selStart, selEnd); } catch { /* egal */ }
    }
  }
}
